import { isModulrAccount } from '@/utils/accounts/is-modulr-account';
import { useEffect } from 'react';
import { useData } from '../../contexts/data-context/use-data';
import { ErrorSection } from '../../design-system/error-section/error-section';
import { Inline } from '../../design-system/inline/inline';
import { LoadingSection } from '../../design-system/loading/loading';
import { Stack } from '../../design-system/stack/stack';
import { TabsContent } from '../../design-system/tabs/tabs-primitives';
import { isBankingAccount } from '../../utils/accounts/is-banking-account';
import { PayrollFlowModal } from './components/payroll-flow-modal';
import { IntroducingPayroll } from './empty-states/introducing-payroll';
import { MissingSeapointAccount } from './empty-states/missing-seapoint-account';
import EmployeeTab from './payroll-section-tabs/employee-tab/employee-tab';
import { PayrollSectionTabs } from './payroll-section-tabs/payroll-section-tabs';
import PayrollTabWrapper from './payroll-section-tabs/payroll-tab/payroll-tab-wrapper';
import { usePayrollFlow } from './use-payroll-flow';

export const PayrollPage = () => {
  const { data, isLoading: isLoadingData, error: dataError } = useData();
  const payrollFlow = usePayrollFlow();
  const { payrollAction, clearPayrollState, updatePayrollAction } = payrollFlow;

  // Handle payment success callback
  useEffect(() => {
    if (data?.payrolls) {
      payrollFlow.handlePaymentSuccessCallback(data.payrolls);
    }
  }, [data?.payrolls, payrollFlow.handlePaymentSuccessCallback]);

  if (isLoadingData) {
    return <LoadingSection text="Loading..." />;
  }

  if (!data) {
    return <ErrorSection error={dataError} />;
  }

  const beneficiaries = data.beneficiaries;

  const employees = data.employees;
  const previousPayrolls = data.payrolls;
  const hasPayrolls = previousPayrolls.length > 0;
  const modulrAccounts = data.accounts.filter(isBankingAccount).filter(isModulrAccount);
  const hasModulrAccount = modulrAccounts.length > 0;
  const companyId = data?.companies[0].id;
  const legalEntityCountry = data?.companies[0].legalEntity.country;
  const beneficiariesMatchingLegalEntityCountry = beneficiaries.filter(
    (beneficiary) => legalEntityCountry && beneficiary.country.toLowerCase() === legalEntityCountry.toLowerCase(),
  );
  const employeesMatchingLegalEntityCountry = employees.filter(
    (employee) => legalEntityCountry && employee.country.toLowerCase() === legalEntityCountry.toLowerCase(),
  );

  const renderMainSection = () => {
    if (hasPayrolls) {
      return (
        <PayrollSectionTabs
          triggers={[
            { value: 'PAYROLLS', counterConfig: { showCounter: false } },
            { value: 'EMPLOYEES', counterConfig: { showCounter: false } },
          ]}
        >
          <TabsContent value="PAYROLLS">
            <PayrollTabWrapper payrollFlow={payrollFlow} payrolls={previousPayrolls} />
          </TabsContent>
          <TabsContent value="EMPLOYEES">
            <EmployeeTab employees={employees} beneficiaries={beneficiariesMatchingLegalEntityCountry} />
          </TabsContent>
        </PayrollSectionTabs>
      );
    }
    if (hasModulrAccount) {
      return <IntroducingPayroll onSetPayrollAction={updatePayrollAction} />;
    }
    return <MissingSeapointAccount />;
  };

  return (
    <Stack align="stretch" className="min-h-screen py-2 pr-2">
      <Stack align="stretch" className="flex-1 overflow-clip rounded-md border border-border bg-background shadow-xs">
        <Stack align="stretch" className="bg-white px-6 pt-4" space={10}>
          <Inline justify="between" className="h-[42px]">
            <h1 className="text-2xl font-semibold text-black">Payroll</h1>
          </Inline>
          {renderMainSection()}
        </Stack>
        {payrollAction && legalEntityCountry && (
          <PayrollFlowModal
            isOpen={!!payrollAction}
            onClose={clearPayrollState}
            payrollAction={payrollAction}
            onSetPayrollAction={updatePayrollAction}
            className="h-full w-full overflow-auto bg-muted"
            legalEntityCountry={legalEntityCountry}
            companyId={companyId}
            beneficiaries={beneficiariesMatchingLegalEntityCountry}
            employees={employeesMatchingLegalEntityCountry}
            isLoading={isLoadingData}
            accounts={data?.accounts.filter(isBankingAccount) || []}
            payrollFlow={payrollFlow}
            payrolls={previousPayrolls}
          />
        )}
      </Stack>
    </Stack>
  );
};
